/**
 * Open-report workspace — several reports stay open at once, like browser tabs, and one report may
 * be open more than once on different filters.
 *
 * A tab is an **instance**, not a report. Its identity is the `tab` query param, minted here and
 * carried in the URL. It has to be in the URL rather than in memory alone: two instances of one
 * report share a route, so without a key in the query string a filter change in the second
 * Spreading would be recorded against the first. The key says only *which tab* a query string
 * belongs to — the answer itself still lives entirely in the other params (§4.4), so a link
 * reproduces the same rows whether or not its recipient has that instance open.
 *
 * The strip is restored from `sessionStorage` after a refresh or a build-version reload, and dies
 * with the browser tab like the token (§6.7). What is stored is which report URLs were open, not
 * any answer: every restored tab is read back from its own query string.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { useAuth } from '@/lib/auth';
import type { FilterFacet } from '@/lib/filters';
import { findMenuItem, menuForRoles, reportPath } from '@/lib/menu';

/** The instance key's query param. No report filter may claim this name. */
const TAB_PARAM = 'tab';

/** How many filter values a tab label carries before it stops being readable at tab width. */
const MAX_LABEL_FACETS = 2;

/** Stands in for a filter a sibling instance sets and this one does not — `<FilterBar>`'s own word. */
const UNSET_LABEL = 'All';

interface StoredTab {
  key: string;
  reportId: string;
  /** Path and query, including this tab's own key. */
  to: string;
  facets: readonly FilterFacet[];
}

export interface WorkspaceTab extends StoredTab {
  /** Menu label, distinguished from a sibling instance only where there is one. */
  label: string;
  /** Every facet, for the tooltip — the label shows at most two. */
  description: string;
}

interface ReportWorkspaceValue {
  tabs: readonly WorkspaceTab[];
  activeKey: string | null;
  closeTab: (key: string) => void;
  activateTab: (key: string) => void;
  /** Open a second instance of a tab, carrying its filters. */
  duplicateTab: (key: string) => void;
  /** Where a menu entry should point: the instance already open, or a fresh one. */
  hrefFor: (reportId: string) => string;
  /** Called by the mounted report to say what its filters are set to. */
  publishFacets: (key: string, facets: readonly FilterFacet[]) => void;
}

const ReportWorkspaceContext = createContext<ReportWorkspaceValue | null>(null);

const REPORT_PATH = /^\/reports\/([^/?#]+)/;

const STORAGE_KEY = 'winsight.report-tabs';

const storedWorkspace = z.object({
  seq: z.number().int().nonnegative(),
  tabs: z.array(
    z.object({
      key: z.string().min(1),
      reportId: z.string().min(1),
      // Anchored: a poisoned entry must not be able to navigate the shell somewhere else.
      to: z.string().regex(REPORT_PATH),
      facets: z.array(z.object({ param: z.string(), label: z.string(), value: z.string() })),
    }),
  ),
});

interface WorkspaceState {
  /** Last minted key number. Persisted so a restored session does not reuse a key. */
  seq: number;
  tabs: StoredTab[];
}

const EMPTY: WorkspaceState = { seq: 0, tabs: [] };

function readStored(): WorkspaceState {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return EMPTY;
    const parsed = storedWorkspace.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : EMPTY;
  } catch {
    return EMPTY;
  }
}

function writeStored(state: WorkspaceState): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* private mode / quota — an unrestored strip is a small loss, not an error */
  }
}

function parseReportId(pathname: string): string | null {
  const match = REPORT_PATH.exec(pathname);
  return match?.[1] ?? null;
}

function splitTo(to: string): { path: string; params: URLSearchParams } {
  const cut = to.indexOf('?');
  return cut === -1
    ? { path: to, params: new URLSearchParams() }
    : { path: to.slice(0, cut), params: new URLSearchParams(to.slice(cut + 1)) };
}

function withKey(to: string, key: string): string {
  const { path, params } = splitTo(to);
  params.set(TAB_PARAM, key);
  return `${path}?${params.toString()}`;
}

/** The same URL as every other instance would spell it — what "already open" is judged on. */
function withoutKey(to: string): string {
  const { path, params } = splitTo(to);
  params.delete(TAB_PARAM);
  const rest = params.toString();
  return rest === '' ? path : `${path}?${rest}`;
}

function mintKey(state: WorkspaceState): { key: string; seq: number } {
  let seq = state.seq;
  let key: string;
  do {
    seq += 1;
    key = `t${String(seq)}`;
  } while (state.tabs.some((tab) => tab.key === key));
  return { key, seq };
}

function sameFacets(a: readonly FilterFacet[], b: readonly FilterFacet[]): boolean {
  return (
    a.length === b.length
    && a.every((facet, index) => facet.param === b[index]?.param && facet.value === b[index]?.value)
  );
}

/** The params whose value is not the same across every instance of one report. */
function differingParams(siblings: readonly StoredTab[]): string[] {
  const ordered: string[] = [];
  for (const sibling of siblings) {
    for (const facet of sibling.facets) {
      if (!ordered.includes(facet.param)) ordered.push(facet.param);
    }
  }
  return ordered.filter((param) => {
    const values = siblings.map(
      (sibling) => sibling.facets.find((facet) => facet.param === param)?.value ?? null,
    );
    return values.some((value) => value !== values[0]);
  });
}

/**
 * Name each tab. A lone instance is just its report; siblings are told apart by the filters that
 * actually differ between them, and fall back to numbering while nothing does — two tabs on the
 * same filters are legitimately the same view twice.
 */
function decorate(
  tabs: readonly StoredTab[],
  menu: ReturnType<typeof menuForRoles>,
): WorkspaceTab[] {
  const byReport = new Map<string, StoredTab[]>();
  for (const tab of tabs) {
    const group = byReport.get(tab.reportId);
    if (group === undefined) byReport.set(tab.reportId, [tab]);
    else group.push(tab);
  }

  return tabs.map((tab) => {
    const base = findMenuItem(menu, tab.reportId)?.item.label ?? tab.reportId;
    const description = tab.facets.map((facet) => `${facet.label}: ${facet.value}`).join(', ');
    const full = description === '' ? base : `${base} — ${description}`;
    const siblings = byReport.get(tab.reportId) ?? [tab];

    if (siblings.length < 2) {
      return { ...tab, label: base, description: full };
    }

    // A tab whose page has not mounted yet — adopted from a pasted link — has nothing to say about
    // its filters, and must not be described as having none of them set.
    const described = siblings.filter((sibling) => sibling.facets.length > 0);
    const distinguishing = differingParams(described).slice(0, MAX_LABEL_FACETS);
    if (distinguishing.length === 0 || tab.facets.length === 0) {
      return {
        ...tab,
        label: `${base} (${String(siblings.indexOf(tab) + 1)})`,
        description: full,
      };
    }

    const shown = distinguishing.map(
      (param) => tab.facets.find((facet) => facet.param === param)?.value ?? UNSET_LABEL,
    );
    return { ...tab, label: `${base} · ${shown.join(' · ')}`, description: full };
  });
}

interface Bound {
  routeKey: string;
  state: WorkspaceState;
  /** The instance the current URL belongs to; null away from a report. */
  activeKey: string | null;
  /**
   * True while `activeKey` has been decided but is not in the address bar yet. A report writes its
   * own params on mount (the factory, so a shared link does not resolve to the recipient's), and a
   * child's effects run before this provider's, so that write lands first. Without this flag the
   * next bind sees a keyless URL it does not recognise and opens a second instance of the report
   * the reader just opened.
   */
  pending: boolean;
}

/**
 * Decide which instance this URL is, opening one if it is none of those already held. Pure, so it
 * can run as a render-time adjustment: the alternative is an effect that writes state on every
 * navigation, which renders the report twice for each move between tabs.
 */
function bind(
  previous: Omit<Bound, 'routeKey'>,
  pathname: string,
  search: string,
): Omit<Bound, 'routeKey'> {
  const { state } = previous;
  const reportId = parseReportId(pathname);
  if (reportId === null) return { state, activeKey: null, pending: false };

  const to = `${pathname}${search}`;
  const requested = new URLSearchParams(search).get(TAB_PARAM);
  const held = requested === null ? undefined : state.tabs.find((tab) => tab.key === requested);

  // The key is still on its way into the URL and the page has changed the query string underneath
  // it. Same navigation, same instance — take the new query string as this tab's.
  if (requested === null && previous.pending && previous.activeKey !== null) {
    const bound = state.tabs.find((tab) => tab.key === previous.activeKey);
    if (bound !== undefined && bound.reportId === reportId) {
      return {
        state: {
          ...state,
          tabs: state.tabs.map((tab) =>
            tab.key === bound.key ? { ...tab, to: withKey(to, bound.key) } : tab,
          ),
        },
        activeKey: bound.key,
        pending: true,
      };
    }
  }

  if (held !== undefined && held.reportId === reportId) {
    return {
      state:
        held.to === to
          ? state
          : {
              ...state,
              tabs: state.tabs.map((tab) => (tab.key === held.key ? { ...tab, to } : tab)),
            },
      activeKey: held.key,
      pending: false,
    };
  }

  // A key this workspace has never seen — a bookmark, or a link from another browser tab. Adopted
  // rather than reminted, so the address the reader followed is the one they keep.
  if (requested !== null && held === undefined) {
    return {
      state: { ...state, tabs: [...state.tabs, { key: requested, reportId, to, facets: [] }] },
      activeKey: requested,
      pending: false,
    };
  }

  // No usable key. An instance already showing this exact URL is the same answer, so bind to it
  // rather than opening a second copy of it; anything else is a new instance.
  const twin = state.tabs.find(
    (tab) => tab.reportId === reportId && withoutKey(tab.to) === withoutKey(to),
  );
  if (twin !== undefined) return { state, activeKey: twin.key, pending: true };

  const { key, seq } = mintKey(state);
  return {
    state: { seq, tabs: [...state.tabs, { key, reportId, to: withKey(to, key), facets: [] }] },
    activeKey: key,
    pending: true,
  };
}

export function ReportWorkspaceProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [, setSearch] = useSearchParams();
  const { user } = useAuth();
  const menu = useMemo(() => menuForRoles(user?.roles ?? []), [user?.roles]);

  const { pathname, search } = location;
  const routeKey = `${pathname}${search}`;
  const [workspace, setWorkspace] = useState<Bound>(() => ({
    routeKey: '',
    state: readStored(),
    activeKey: null,
    pending: false,
  }));

  // Derived-state adjustment during render: navigation already causes it, so no effect is needed.
  let { state, activeKey, pending } = workspace;
  if (workspace.routeKey !== routeKey) {
    ({ state, activeKey, pending } = bind(workspace, pathname, search));
    setWorkspace({ routeKey, state, activeKey, pending });
  }

  const setState = useCallback((update: (prev: WorkspaceState) => WorkspaceState) => {
    setWorkspace((prev) => {
      const next = update(prev.state);
      return next === prev.state ? prev : { ...prev, state: next };
    });
  }, []);

  /**
   * Put the bound key in the address bar. Written through the params updater rather than as a
   * navigation to a URL built here: the report writes its own params from its own effects, which
   * run first, and a rewritten whole URL would drop whatever it had just set.
   */
  useEffect(() => {
    if (activeKey === null || !pending) return;
    setSearch(
      (current) => {
        const params = new URLSearchParams(current);
        params.set(TAB_PARAM, activeKey);
        return params;
      },
      { replace: true },
    );
  }, [activeKey, pending, setSearch]);

  useEffect(() => {
    writeStored(state);
  }, [state]);

  // The page and its `<FilterBar>` both hold the same filters and both publish. Unchanged values
  // must therefore return the state untouched, or the two would re-render each other in turn.
  const publishFacets = useCallback((key: string, facets: readonly FilterFacet[]) => {
    setState((prev) => {
      const current = prev.tabs.find((tab) => tab.key === key);
      if (current === undefined || sameFacets(current.facets, facets)) return prev;
      return {
        ...prev,
        tabs: prev.tabs.map((tab) => (tab.key === key ? { ...tab, facets } : tab)),
      };
    });
  }, [setState]);

  const activateTab = useCallback(
    (key: string) => {
      const tab = state.tabs.find((candidate) => candidate.key === key);
      if (tab !== undefined) navigate(tab.to);
    },
    [state.tabs, navigate],
  );

  const duplicateTab = useCallback(
    (key: string) => {
      const index = state.tabs.findIndex((candidate) => candidate.key === key);
      const source = state.tabs[index];
      if (source === undefined) return;

      const { key: copyKey, seq } = mintKey(state);
      const to = withKey(source.to, copyKey);
      setState((prev) => {
        const tabs = [...prev.tabs];
        // Beside the tab it came from: a copy that appears at the far end reads as a new report.
        tabs.splice(index + 1, 0, { ...source, key: copyKey, to });
        return { seq: Math.max(prev.seq, seq), tabs };
      });
      navigate(to);
    },
    [state, navigate, setState],
  );

  const closeTab = useCallback(
    (key: string) => {
      const index = state.tabs.findIndex((tab) => tab.key === key);
      if (index === -1) return;
      const next = state.tabs.filter((tab) => tab.key !== key);
      setState((prev) => ({ ...prev, tabs: prev.tabs.filter((tab) => tab.key !== key) }));

      if (activeKey === key) {
        const neighbour = next[index] ?? next[index - 1] ?? null;
        navigate(neighbour === null ? '/' : neighbour.to);
      }
    },
    [state.tabs, activeKey, navigate, setState],
  );

  const hrefFor = useCallback(
    (reportId: string) =>
      state.tabs.find((tab) => tab.reportId === reportId)?.to ?? reportPath(reportId),
    [state.tabs],
  );

  const tabs = useMemo(() => decorate(state.tabs, menu), [state.tabs, menu]);

  const value = useMemo(
    () => ({
      tabs,
      activeKey,
      closeTab,
      activateTab,
      duplicateTab,
      hrefFor,
      publishFacets,
    }),
    [tabs, activeKey, closeTab, activateTab, duplicateTab, hrefFor, publishFacets],
  );

  return (
    <ReportWorkspaceContext.Provider value={value}>{children}</ReportWorkspaceContext.Provider>
  );
}

export function useReportWorkspace(): ReportWorkspaceValue {
  const ctx = useContext(ReportWorkspaceContext);
  if (ctx === null) {
    throw new Error('useReportWorkspace must be used inside ReportWorkspaceProvider');
  }
  return ctx;
}

/**
 * Publish the mounted report's filter values to its own tab, so the strip can tell two instances
 * apart. Optional by design: outside a workspace (a test rendering a page on its own) it does
 * nothing. Called from `useReportFilters`, which is where the resolved option labels are.
 */
export function usePublishTabFacets(facets: readonly FilterFacet[]): void {
  const ctx = useContext(ReportWorkspaceContext);
  const activeKey = ctx?.activeKey ?? null;
  const publishFacets = ctx?.publishFacets;
  // The values, not the array identity: `useReportFilters` rebuilds these on every render.
  const signature = facets.map((facet) => `${facet.param} ${facet.value}`).join('');

  useEffect(() => {
    if (activeKey === null || publishFacets === undefined) return;
    publishFacets(activeKey, facets);
    // `facets` is a fresh array each render; `signature` is what actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, publishFacets, signature]);
}
